import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Spinner } from './shared';

const AUTO_REFRESH_INTERVAL = 15000;

function formatUptime(startedAt) {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now - start;
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function StatusBadge({ connected }) {
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
            {connected ? 'Connected' : 'Disconnected'}
        </span>
    );
}

function MetricBar({ label, value, max, unit = '' }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    const color = pct > 80 ? 'bg-red-500' : pct > 60 ? 'bg-yellow-500' : 'bg-blue-500';
    return (
        <div className="flex items-center gap-3 text-xs">
            <span className="w-16 text-gray-500 shrink-0">{label}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="w-16 text-right text-gray-700 font-mono">{value}{unit}</span>
        </div>
    );
}

function NodeCard({ node }) {
    const [showDetail, setShowDetail] = useState(false);
    const sys = node.system_stats || {};
    const cache = node.cache_stats || {};
    const sync = node.sync_stats || {};
    const details = node.cache_details || {};
    const resolvedData = details.resolved_configs;
    const compiledKeys = details.compiled_agents || [];

    return (
        <>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-gray-900 text-white text-xs font-mono rounded">PID {node.pid}</span>
                        <span className="text-xs text-gray-500">up {formatUptime(node.started_at)}</span>
                    </div>
                    <StatusBadge connected={sync.connected} />
                </div>

                {Object.keys(sys).length > 0 && (
                    <div className="px-4 py-3 border-b border-gray-100 space-y-2">
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">System</p>
                        <MetricBar label="CPU" value={sys.cpu_percent || 0} max={100} unit="%" />
                        <MetricBar label="RAM" value={sys.memory_rss_mb || 0} max={1024} unit=" MB" />
                        <div className="flex items-center gap-3 text-xs">
                            <span className="w-16 text-gray-500">Threads</span>
                            <span className="text-gray-700 font-mono">{sys.threads || 0}</span>
                            <span className="w-16 text-gray-500 ml-4">Mem %</span>
                            <span className="text-gray-700 font-mono">{sys.memory_percent || 0}%</span>
                        </div>
                    </div>
                )}

                <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Cache</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {Object.entries(cache).map(([key, count]) => (
                            <div key={key} className="flex items-center justify-between text-xs">
                                <span className="text-gray-600">{key}</span>
                                <span className="font-mono text-gray-900">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Sync</p>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Listener</span>
                            <span className={`font-mono ${sync.listener_active ? 'text-green-600' : 'text-red-500'}`}>{sync.listener_active ? 'active' : 'inactive'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Received</span>
                            <span className="font-mono text-gray-900">{sync.messages_received || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Sent</span>
                            <span className="font-mono text-gray-900">{sync.messages_sent || 0}</span>
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3">
                    <button
                        onClick={() => setShowDetail(true)}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                        View Detail
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                    </button>
                </div>
            </div>

            {showDetail && (
                <NodeDetailModal
                    node={node}
                    resolvedData={resolvedData}
                    compiledKeys={compiledKeys}
                    onClose={() => setShowDetail(false)}
                />
            )}
        </>
    );
}

function NodeDetailModal({ node, resolvedData, compiledKeys, onClose }) {
    const stats = resolvedData?.stats || {};
    const agents = resolvedData?.agents || {};
    const perAgent = stats.per_agent || {};
    const resolvedCompiledKeys = resolvedData?.compiled_keys || compiledKeys || [];

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 overflow-auto" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] overflow-auto m-4" onClick={e => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                    <div className="flex items-center gap-3">
                        <span className="px-2 py-0.5 bg-gray-900 text-white text-xs font-mono rounded">PID {node.pid}</span>
                        <span className="text-sm text-gray-500">Resolved Agent Cache</span>
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{stats.resolved_configs || 0} configs</span>
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">{stats.compiled_agents || 0} compiled</span>
                        {stats.total_size_mb > 0 && (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded">{stats.total_size_mb} MB</span>
                        )}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {stats.total_size_mb > 0 && (
                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-2xl font-mono font-semibold text-gray-900">{stats.total_size_mb} <span className="text-sm text-gray-400">MB</span></p>
                                <p className="text-xs text-gray-500 mt-0.5">Total</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-lg font-mono text-gray-700">{((stats.configs_size_bytes || 0) / 1024).toFixed(1)} <span className="text-sm text-gray-400">KB</span></p>
                                <p className="text-xs text-gray-500 mt-0.5">Configs</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-lg font-mono text-gray-700">{((stats.compiled_size_bytes || 0) / 1024 / 1024).toFixed(2)} <span className="text-sm text-gray-400">MB</span></p>
                                <p className="text-xs text-gray-500 mt-0.5">Compiled</p>
                            </div>
                        </div>
                    )}

                    {Object.keys(perAgent).length > 0 && (
                        <div className="space-y-1.5">
                            <p className="text-xs text-gray-400 uppercase tracking-wider">Per Agent</p>
                            {Object.entries(perAgent).map(([aid, info]) => (
                                <div key={aid} className="flex items-center gap-3 text-xs bg-gray-50 rounded px-3 py-2">
                                    <span className="font-mono font-medium text-gray-700 w-32">{aid}</span>
                                    <span className="font-mono text-gray-500">{info.size_kb} KB</span>
                                    <div className="flex gap-1 ml-auto flex-wrap">
                                        {Object.entries(info).filter(([k]) => k !== 'size_bytes' && k !== 'size_kb').map(([k, v]) => (
                                            <span key={k} className="px-1.5 py-0.5 bg-white text-gray-500 rounded text-xs border">{k}: {v}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {resolvedCompiledKeys.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Compiled Agents</p>
                            <div className="flex flex-wrap gap-1.5">
                                {resolvedCompiledKeys.map(k => (
                                    <span key={k} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded font-mono">{k}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {Object.entries(agents).map(([agentId, agent]) => (
                        <AgentSummaryCard key={agentId} agentId={agentId} agent={agent} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function AgentSummaryCard({ agentId, agent }) {
    const [open, setOpen] = useState(false);
    const [promptView, setPromptView] = useState(false);

    return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50">
                <svg className={`w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="font-mono font-medium text-gray-900">{agentId}</span>
                <span className="text-xs text-gray-500">{agent.agent_type}</span>
                <span className="text-xs font-mono text-gray-400">{agent.model_name}</span>
                <div className="ml-auto flex gap-1.5 flex-wrap">
                    {[
                        ['skills', agent.skills_count],
                        ['subagents', agent.subagents?.length],
                        ['tools', agent.tools_count],
                        ['reminders', agent.reminders?.length],
                        ['knowledge', agent.knowledge?.length],
                        ['mcp', agent.mcp_servers?.length],
                    ].map(([label, count]) => (
                        <span key={label} className={`px-1.5 py-0.5 rounded text-xs ${count > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                            {label} {count || 0}
                        </span>
                    ))}
                </div>
            </button>

            {open && (
                <div className="border-t border-gray-100 divide-y divide-gray-100">
                    <div className="px-5 py-3 grid grid-cols-2 gap-x-8 gap-y-2 text-xs bg-gray-50">
                        <div className="flex gap-2"><span className="text-gray-400 w-28">Model</span><span className="font-mono text-gray-700">{agent.model_name}</span></div>
                        <div className="flex gap-2"><span className="text-gray-400 w-28">Agent Type</span><span className="text-gray-700">{agent.agent_type}</span></div>
                        <div className="flex gap-2"><span className="text-gray-400 w-28">Memory</span><span className="text-gray-700">{(agent.memory || []).join(', ')}</span></div>
                        <div className="flex gap-2"><span className="text-gray-400 w-28">Resolved At</span><span className="font-mono text-gray-700">{new Date(agent.resolved_at).toLocaleString()}</span></div>
                        <div className="flex gap-2"><span className="text-gray-400 w-28">HITL</span><span className="text-gray-700">{agent.hitl_enabled ? `enabled (${agent.interrupt_tools_count} tools)` : 'disabled'}</span></div>
                        <div className="flex gap-2"><span className="text-gray-400 w-28">System Prompt</span><span className="font-mono text-gray-700">{agent.system_prompt ? `${agent.system_prompt.length} chars` : 'none'}</span></div>
                        <div className="flex gap-2"><span className="text-gray-400 w-28">Brand</span><span className="font-mono text-gray-700 text-xs">{agent.brand ? JSON.stringify(agent.brand) : 'none'}</span></div>
                    </div>

                    {agent.system_prompt && (
                        <div className="px-5 py-3">
                            <button onClick={() => setPromptView(!promptView)} className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 mb-2">
                                <svg className={`w-3 h-3 transition-transform ${promptView ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="uppercase tracking-wider font-medium">System Prompt</span>
                            </button>
                            {promptView && (
                                <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700 bg-gray-50 p-3 rounded max-h-96 overflow-auto">{agent.system_prompt}</pre>
                            )}
                        </div>
                    )}

                    <ListSection title="Skills" items={agent.skills} />
                    <ListSection title="Subagents" items={agent.subagents} />
                    <ListSection title={`Tools (${agent.tools_count})`} items={agent.tools_sample} suffix={agent.tools_count > 10 ? `... +${agent.tools_count - 10} more` : ''} />
                    <ListSection title="Reminders" items={agent.reminders} />
                    <ListSection title="Knowledge" items={agent.knowledge} />
                    <ListSection title="MCP Servers" items={agent.mcp_servers} />
                </div>
            )}
        </div>
    );
}

function ListSection({ title, items, suffix }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="px-5 py-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">{title}</p>
            <div className="flex flex-wrap gap-1">
                {items.map(id => (
                    <span key={id} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded font-mono">{id}</span>
                ))}
                {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
            </div>
        </div>
    );
}

function AutoAgentDetail({ agentId, detail }) {
    const [open, setOpen] = useState(false);

    return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50"
            >
                <svg className={`w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="font-mono font-medium text-gray-900">{agentId}</span>
                {typeof detail === 'object' && !Array.isArray(detail) && (
                    <span className="text-xs text-gray-400 ml-auto">{Object.keys(detail).length} keys</span>
                )}
            </button>
            {open && (
                <div className="border-t border-gray-100 divide-y divide-gray-100">
                    {typeof detail === 'object' && detail !== null ? (
                        Object.entries(detail).map(([key, value]) => (
                            <AutoSection key={key} sectionKey={key} value={value} />
                        ))
                    ) : (
                        <div className="px-5 py-3 text-xs text-gray-500 font-mono">{String(detail)}</div>
                    )}
                </div>
            )}
        </div>
    );
}

function AutoSection({ sectionKey, value }) {
    const [open, setOpen] = useState(false);

    if (value === null || value === undefined) return null;

    if (Array.isArray(value)) {
        return (
            <div>
                <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-gray-50">
                    <svg className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{sectionKey}</span>
                    <span className="text-xs text-gray-400 font-mono">{value.length} items</span>
                </button>
                {open && (
                    <div className="px-5 pb-3 space-y-1">
                        {value.length === 0 ? <p className="text-xs text-gray-400">empty</p> : value.map((item, i) => (
                            <AutoItem key={item?.id || item?.name || i} item={item} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (typeof value === 'object') {
        return (
            <div>
                <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-gray-50">
                    <svg className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{sectionKey}</span>
                    <span className="text-xs text-gray-400 font-mono">{Object.keys(value).length} keys</span>
                </button>
                {open && (
                    <div className="px-5 pb-3">
                        <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700 bg-gray-50 p-3 rounded max-h-96 overflow-auto">
                            {JSON.stringify(value, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        );
    }

    if (typeof value === 'string' && value.length > 200) {
        return (
            <div>
                <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-gray-50">
                    <svg className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{sectionKey}</span>
                    <span className="text-xs text-gray-400 font-mono">{value.length} chars</span>
                </button>
                {open && (
                    <div className="px-5 pb-3">
                        <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700 bg-gray-50 p-3 rounded max-h-96 overflow-auto">{value}</pre>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex gap-2 px-5 py-2 text-xs">
            <span className="text-gray-400 w-28 shrink-0 uppercase">{sectionKey}</span>
            <span className="text-gray-700 font-mono">{String(value)}</span>
        </div>
    );
}

function AutoItem({ item }) {
    const [open, setOpen] = useState(false);
    if (typeof item !== 'object' || item === null) {
        return <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded font-mono">{String(item)}</span>;
    }
    const id = item.id || item.name || '';
    const entries = Object.entries(item).filter(([k]) => k !== 'id' && k !== 'name');
    return (
        <div className="rounded border border-gray-100 bg-gray-50">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-gray-100">
                <svg className={`w-2.5 h-2.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="font-mono text-gray-600 truncate">{id}</span>
                {item.name && item.name !== item.id && <span className="text-gray-500 truncate">{item.name}</span>}
            </button>
            {open && (
                <div className="px-4 py-2 border-t border-gray-100 text-xs space-y-0.5 max-h-80 overflow-auto">
                    {entries.map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                            <span className="text-gray-400 w-28 shrink-0">{k}</span>
                            <span className="text-gray-600 font-mono break-all whitespace-pre-wrap">
                                {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v ?? '')}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminMonitor() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reloading, setReloading] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const timerRef = useRef(null);

    const fetchNodes = useCallback(async (showLoading = false) => {
        if (showLoading) setLoading(true);
        try {
            const res = await adminApi.getMonitorNodes();
            setData(res);
        } catch (e) {
            console.error('Failed to fetch monitor nodes:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchNodes(true); }, [fetchNodes]);

    useEffect(() => {
        if (autoRefresh) {
            timerRef.current = setInterval(() => fetchNodes(), AUTO_REFRESH_INTERVAL);
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [autoRefresh, fetchNodes]);

    const handleReload = async () => {
        setReloading(true);
        try {
            await adminApi.reloadAllCaches();
            setTimeout(() => fetchNodes(), 1000);
        } catch (e) {
            console.error('Failed to reload caches:', e);
        } finally {
            setReloading(false);
        }
    };

    const nodes = data?.nodes || [];

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Server Monitor</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {nodes.length} worker{nodes.length !== 1 ? 's' : ''} running
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                        <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)}
                            className="rounded border-gray-300 text-gray-900 focus:ring-gray-500" />
                        Auto-refresh
                    </label>
                    <button onClick={handleReload} disabled={reloading}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50">
                        {reloading ? 'Reloading...' : 'Reload All'}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><Spinner size="lg" text="Loading nodes..." /></div>
            ) : nodes.length === 0 ? (
                <div className="text-center py-20 text-gray-500">
                    <p className="text-lg font-medium">No worker nodes detected</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {nodes.map((node) => <NodeCard key={node.pid} node={node} />)}
                </div>
            )}
        </div>
    );
}
